import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-xl font-bold text-foreground-950 whitespace-nowrap">
              LitBuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Home
            </Link>

            <Link to="/litbuy-spreadsheet" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Ultimate Guide
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-lg border border-background-200 py-2 shadow-sm z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/guides/what-is-litbuy-spreadsheet" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Guides
            </Link>
            <Link to="/comparisons/litbuy-vs-google-sheets" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap">
              Comparisons
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-accent-500 hover:text-accent-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              litbuy spreadsheet
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
          <div className="px-4 py-6 flex flex-col gap-3">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-50 transition-colors"
            >
              <i className="ri-home-4-line text-lg text-primary-500"></i>
              Home
            </Link>

            <Link
              to="/litbuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-50 transition-colors"
            >
              <i className="ri-book-open-line text-lg text-primary-500"></i>
              Ultimate Guide
            </Link>

            {/* Mobile Categories - Expandable */}
            <div className="border-t border-background-100 pt-3 mt-1">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg text-accent-500"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="ml-6 mt-1 border-l-2 border-background-100 pl-4 flex flex-col gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-700 py-2.5 px-2 rounded-md hover:text-primary-500 hover:bg-background-50 transition-colors"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guides/what-is-litbuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-50 transition-colors"
            >
              <i className="ri-guide-line text-lg text-primary-500"></i>
              Guides
            </Link>

            <Link
              to="/comparisons/litbuy-vs-google-sheets"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-3 px-3 rounded-lg hover:bg-background-50 transition-colors"
            >
              <i className="ri-scales-3-line text-lg text-primary-500"></i>
              Comparisons
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 text-base font-semibold text-accent-500 py-3 px-3 rounded-lg hover:bg-background-50 transition-colors"
            >
              <i className="ri-shopping-bag-3-line text-lg"></i>
              Shopping Guide
            </a>

            <div className="border-t border-background-100 pt-4 mt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full px-5 py-3.5 bg-primary-500 text-white rounded-full text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                litbuy spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full mt-3 px-5 py-3.5 bg-white border-2 border-foreground-200 text-foreground-900 rounded-full text-sm font-semibold text-center hover:border-primary-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}